// src/utils/checklistTemplateDraft.js
// chklstbuild-6c1e9b42 — CHECKLISTS SEAM 6 (Settings › Checklists builder, web).
//
// The builder's pure logic: the draft a person edits, the exact save request, and a
// validator that MIRRORS functions/utils/checklists.js validateTemplateInput so Save
// rarely fails on the server. The server stays the gate: saveChecklistTemplate
// re-validates everything, and firestore.rules keep checklistTemplates server-write-only.
// checklistTemplateDraft.test.js checks this validator against the server's own.
//
// No Firebase imports here, so it is testable offline.

// ─── roll-out switch ────────────────────────────────────────────────────────
// The builder is shown only to the companies below until ALL of: 2.3.0 is live in both
// stores, the phone minimum update is raised to 10 or higher for every company (update 10 carries
// Seam 6b — Pick as many, notes, attached photos: chklstmulti-3e9b7c15), and companies using
// signatures require app version 2.3.0. Then set CHECKLIST_BUILDER_FOR_EVERYONE = true
// (one web deploy). Display only — see the security note in the Seam 6 plan.
// chklaunch-a90283b6 — LAUNCH (Marcus, 2026-10-26): ON for every company. 2.3.0 is live in both stores, update 12
// is published to 2.3.0 / 2.2.0 / 2.1.0, and the minimum update goes to 12 for everyone with setMobileAppMinimum.js.
// Signatures still need the 2.3.0 store app (older apps show "needs the newest version"); no app-version minimum yet.
export const CHECKLIST_BUILDER_FOR_EVERYONE = true;
export const CHECKLIST_BUILDER_ORG_IDS = Object.freeze(['YqmfHuIhV5fOlp0YFc2y']); // Spatchy Test Company
export const isChecklistBuilderOn = (orgId) =>
    CHECKLIST_BUILDER_FOR_EVERYONE || (!!orgId && CHECKLIST_BUILDER_ORG_IDS.includes(orgId));

// ─── mirrors of functions/utils/checklists.js (change together) ─────────────
export const CHECKLIST_LIMITS = Object.freeze({
    templateName: 120, sectionTitle: 120, questionLabel: 300, optionText: 100, unit: 20,
    options: 50, sections: 30, questions: 100, templateBytes: 200 * 1024, templatesPerOrg: 100,
});
const ID_RE = /^[A-Za-z0-9_-]{1,40}$/;
const AUTO_ATTACH_VALUES = ['every_job', 'manual'];
export const REFUSE_STALE_TEXT = 'Someone else changed this checklist template. Reload it and try again.';

// The 11 question types, in the order the "Add a question" grid shows them.
export const QUESTION_TYPE_ORDER = Object.freeze(['short', 'long', 'yesno', 'choice', 'multi', 'checkbox', 'number', 'photos', 'date', 'signature', 'info']); // multi: chklstmulti-3e9b7c15
export const QUESTION_TYPE_LABELS = Object.freeze({
    short: 'Short answer',
    long: 'Long answer',
    yesno: 'Yes / No / N/A',
    choice: 'Pick one',
    multi: 'Pick as many as apply',
    checkbox: 'Tick box',
    number: 'Number',
    photos: 'Photos',
    date: 'Date',
    signature: 'Signature',
    info: 'Instructions',
});

// One-line hint under each type in the "+ Add question" picker.
export const QUESTION_TYPE_HINTS = Object.freeze({
    short: 'a few words',
    long: 'a paragraph',
    yesno: 'three quick buttons',
    choice: 'choose from your list',
    multi: 'tick all that apply',
    checkbox: 'one box to tick when done',
    number: 'with an optional unit (m, A…)',
    photos: 'take or attach up to 10',
    date: 'pick a date',
    signature: 'customer or tech signs',
    info: 'text for the tech, no answer',
});

// chklstmulti-3e9b7c15 — mirrors of the server's OPTION_TYPES and per-question switch rules.
export const OPTION_TYPES = Object.freeze(['choice', 'multi']);
export const canAllowNote = (type) => type !== 'info';
export const canAllowPhotos = (type) => !['info', 'photos', 'signature'].includes(type);

// ─── small helpers ──────────────────────────────────────────────────────────
// UTF-8 byte length (the server measures Buffer.byteLength of the JSON).
const utf8Bytes = (s) => {
    let n = 0;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c < 0x80) n += 1;
        else if (c < 0x800) n += 2;
        else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) { n += 4; i++; }
        else n += 3;
    }
    return n;
};

const str = (v) => (typeof v === 'string' ? v : '');

// "Pass, Fail" → ['Pass', 'Fail']. Empty pieces (",,") are dropped.
export const splitOptions = (text) => str(text).split(',').map((o) => o.trim()).filter(Boolean);

// Every section and question id in the draft.
export const takenIds = (draft) => {
    const ids = new Set();
    for (const s of draft?.sections || []) {
        ids.add(s.id);
        for (const q of s.questions || []) ids.add(q.id);
    }
    return ids;
};

// A new id matching the server's ID_RE that is not already in the template.
export const newId = (prefix, taken = new Set()) => {
    for (;;) {
        let r = '';
        while (r.length < 8) r += Math.random().toString(36).slice(2);
        const id = `${prefix}_${r.slice(0, 8)}`;
        if (!taken.has(id)) return id;
    }
};

// ─── drafts ─────────────────────────────────────────────────────────────────
// Draft: { templateId, version, name, autoAttach, blocksCompletion, customerCopy,
//          sections: [{ id, title, questions: [{ id, type, label, required, optionsText, unit, allowNote, allowPhotos, officeOnly }] }] }
// version 0 = not saved yet (saveChecklistTemplate's expectedVersion 0 = create).

// custcopy-32471cbd — "Customers can receive a copy": ON for a new checklist; a saved one
// without the field (every template from before Seam 8) opens OFF, matching the server.
export const emptyDraft = () => ({
    templateId: null,
    version: 0,
    name: '',
    autoAttach: 'manual',
    blocksCompletion: false,
    customerCopy: true,
    sections: [{ id: newId('s'), title: '', questions: [] }],
});

export const draftFromTemplate = (templateId, doc) => ({
    templateId,
    version: Number.isInteger(doc?.version) && doc.version > 0 ? doc.version : 1,
    name: str(doc?.name),
    autoAttach: doc?.autoAttach === 'every_job' ? 'every_job' : 'manual',
    blocksCompletion: doc?.blocksCompletion === true,
    customerCopy: doc?.customerCopy === true, // custcopy-32471cbd
    sections: (doc?.sections || []).map((s) => ({
        id: s.id,
        title: str(s.title),
        questions: (s.questions || []).map((q) => ({
            id: q.id,
            type: q.type,
            label: str(q.label),
            required: q.required === true,
            optionsText: Array.isArray(q.options) ? q.options.join(', ') : '',
            unit: str(q.unit),
            allowNote: q.allowNote === true,     // chklstmulti-3e9b7c15
            allowPhotos: q.allowPhotos === true,
            officeOnly: q.officeOnly === true,   // custcopy-32471cbd
        })),
    })),
});

// "{name} (copy)", shortened so the whole name fits the server's limit.
export const suffixedName = (name, suffix) => {
    const room = CHECKLIST_LIMITS.templateName - suffix.length;
    return `${str(name).trim().slice(0, room).trimEnd()}${suffix}`;
};

// Duplicate: a NEW unsaved checklist, added by hand, not blocking.
export const duplicateDraft = (doc) => ({
    ...draftFromTemplate(null, doc),
    templateId: null,
    version: 0,
    name: suffixedName(doc?.name, ' (copy)'),
    autoAttach: 'manual',
    blocksCompletion: false,
});

// The exact content saveChecklistTemplate validates (the caller adds templateId and
// expectedVersion). Same keys as the server's normalized output.
export const toSaveInput = (draft) => ({
    name: str(draft.name).trim(),
    autoAttach: draft.autoAttach,
    blocksCompletion: draft.blocksCompletion === true,
    customerCopy: draft.customerCopy === true, // custcopy-32471cbd
    sections: (draft.sections || []).map((s) => ({
        id: s.id,
        title: str(s.title).trim(),
        questions: (s.questions || []).map((q) => {
            const out = { id: q.id, type: q.type, label: str(q.label).trim(), required: q.type === 'info' ? false : q.required === true };
            if (OPTION_TYPES.includes(q.type)) out.options = splitOptions(q.optionsText); // chklstmulti-3e9b7c15
            if (q.type === 'number' && str(q.unit).trim()) out.unit = str(q.unit).trim();
            if (q.allowNote === true && canAllowNote(q.type)) out.allowNote = true;
            if (q.allowPhotos === true && canAllowPhotos(q.type)) out.allowPhotos = true;
            if (q.officeOnly === true) out.officeOnly = true; // custcopy-32471cbd — any type
            return out;
        }),
    })),
});

export const isDirty = (base, draft) =>
    !base || JSON.stringify(toSaveInput(base)) !== JSON.stringify(toSaveInput(draft));

// "N questions · M required". Instructions lines are not questions.
export const countQuestions = (sections) => {
    let questions = 0, required = 0;
    for (const s of sections || []) {
        for (const q of s.questions || []) {
            if (q.type === 'info') continue;
            questions++;
            if (q.required === true) required++;
        }
    }
    return { questions, required };
};

export const totalItems = (draft) => (draft?.sections || []).reduce((n, s) => n + (s.questions || []).length, 0);

export const questionSummary = (sections) => {
    const { questions, required } = countQuestions(sections);
    return `${questions} question${questions === 1 ? '' : 's'} · ${required} required`;
};

// ─── validation (mirror of validateTemplateInput) ───────────────────────────
// Returns { ok, form: [msg], name: msg|null, bySection: { id: msg }, byQuestion: { id: [msg] } }.
export const validateDraft = (draft) => {
    const L = CHECKLIST_LIMITS;
    const form = [];
    const bySection = {};
    const byQuestion = {};
    const addQ = (id, msg) => { (byQuestion[id] = byQuestion[id] || []).push(msg); };

    const nameT = str(draft?.name).trim();
    let name = null;
    if (!nameT) name = 'Give this checklist a name.';
    else if (nameT.length > L.templateName) name = `The name must be ${L.templateName} characters or fewer.`;

    if (!AUTO_ATTACH_VALUES.includes(draft?.autoAttach)) form.push('Choose which jobs get this checklist.');

    const sections = draft?.sections || [];
    if (sections.length < 1) form.push('A checklist needs at least one section.');
    if (sections.length > L.sections) form.push(`A checklist can have at most ${L.sections} sections.`);

    const sectionIds = new Set();
    const questionIds = new Set();
    let badIds = false;
    let count = 0;
    for (const s of sections) {
        if (typeof s.id !== 'string' || !ID_RE.test(s.id) || sectionIds.has(s.id)) badIds = true;
        sectionIds.add(s.id);
        if (str(s.title).trim().length > L.sectionTitle) bySection[s.id] = `The section title must be ${L.sectionTitle} characters or fewer.`;
        for (const q of s.questions || []) {
            count++;
            if (typeof q.id !== 'string' || !ID_RE.test(q.id) || questionIds.has(q.id)) badIds = true;
            questionIds.add(q.id);
            if (!QUESTION_TYPE_ORDER.includes(q.type)) { addQ(q.id, 'This question has an unknown type. Delete it and add it again.'); continue; }
            const label = str(q.label).trim();
            if (!label) addQ(q.id, q.type === 'info' ? 'Type the instructions.' : 'Type the question.');
            else if (label.length > L.questionLabel) addQ(q.id, `Keep this to ${L.questionLabel} characters or fewer.`);
            if (OPTION_TYPES.includes(q.type)) {
                const opts = splitOptions(q.optionsText);
                if (opts.length < 1) addQ(q.id, 'Add at least one choice, separated by commas.');
                else if (opts.length > L.options) addQ(q.id, `At most ${L.options} choices.`);
                if (opts.some((o) => o.length > L.optionText)) addQ(q.id, `Each choice must be ${L.optionText} characters or fewer.`);
                const seen = new Set();
                for (const o of opts) {
                    if (seen.has(o)) { addQ(q.id, `"${o}" appears twice.`); break; }
                    seen.add(o);
                }
            }
            if (q.type === 'number' && str(q.unit).trim().length > L.unit) addQ(q.id, `The unit must be ${L.unit} characters or fewer.`);
        }
    }
    if (badIds) form.push("Something is wrong with this checklist's internal ids. Reload the page and try again.");
    if (count < 1) form.push('Add at least one question.');
    if (count > L.questions) form.push(`A checklist can have at most ${L.questions} questions.`);

    const clean = !name && !form.length && !Object.keys(bySection).length && !Object.keys(byQuestion).length;
    if (clean && utf8Bytes(JSON.stringify(toSaveInput(draft))) > L.templateBytes) {
        form.push('This checklist is too large to save. Shorten some questions or choices.');
    }
    const ok = !name && !form.length && !Object.keys(bySection).length && !Object.keys(byQuestion).length;
    return { ok, form, name, bySection, byQuestion };
};

// ─── edits (pure; each returns a new draft) ─────────────────────────────────
const mapSections = (draft, fn) => ({ ...draft, sections: draft.sections.map(fn) });

export const addSection = (draft) => {
    const id = newId('s', takenIds(draft));
    return { draft: { ...draft, sections: [...draft.sections, { id, title: '', questions: [] }] }, id };
};

export const updateSection = (draft, sectionId, patch) =>
    mapSections(draft, (s) => (s.id === sectionId ? { ...s, ...patch } : s));

// Never removes the last section.
export const removeSection = (draft, sectionId) =>
    draft.sections.length <= 1 ? draft : { ...draft, sections: draft.sections.filter((s) => s.id !== sectionId) };

export const newQuestion = (type, taken) => ({
    id: newId('q', taken), type, label: '', required: false, optionsText: '', unit: '', allowNote: false, allowPhotos: false, officeOnly: false,
});

export const addQuestion = (draft, sectionId, type) => {
    const q = newQuestion(type, takenIds(draft));
    const target = draft.sections.some((s) => s.id === sectionId) ? sectionId : draft.sections[0].id;
    return { draft: mapSections(draft, (s) => (s.id === target ? { ...s, questions: [...s.questions, q] } : s)), id: q.id };
};

export const updateQuestion = (draft, questionId, patch) =>
    mapSections(draft, (s) => ({ ...s, questions: s.questions.map((q) => (q.id === questionId ? { ...q, ...patch } : q)) }));

export const removeQuestion = (draft, questionId) =>
    mapSections(draft, (s) => ({ ...s, questions: s.questions.filter((q) => q.id !== questionId) }));

const locate = (draft, questionId) => {
    for (let si = 0; si < draft.sections.length; si++) {
        const qi = draft.sections[si].questions.findIndex((q) => q.id === questionId);
        if (qi !== -1) return { si, qi };
    }
    return null;
};

// Up/down stays INSIDE the question's section: no move past its top or bottom.
export const canMoveQuestion = (draft, questionId, dir) => {
    const at = locate(draft, questionId);
    if (!at) return false;
    const j = at.qi + dir;
    return j >= 0 && j < draft.sections[at.si].questions.length;
};

export const moveQuestion = (draft, questionId, dir) => {
    if (!canMoveQuestion(draft, questionId, dir)) return draft;
    const at = locate(draft, questionId);
    const sections = draft.sections.map((s) => ({ ...s, questions: [...s.questions] }));
    const list = sections[at.si].questions;
    const j = at.qi + dir;
    [list[at.qi], list[j]] = [list[j], list[at.qi]];
    return { ...draft, sections };
};

// "Move to section": the question goes to the END of the target section. Moving to its own
// section, or to a section that doesn't exist, changes nothing.
export const moveQuestionToSection = (draft, questionId, targetSectionId) => {
    const at = locate(draft, questionId);
    const ti = draft.sections.findIndex((s) => s.id === targetSectionId);
    if (!at || ti === -1 || ti === at.si) return draft;
    const sections = draft.sections.map((s) => ({ ...s, questions: [...s.questions] }));
    const [q] = sections[at.si].questions.splice(at.qi, 1);
    sections[ti].questions.push(q);
    return { ...draft, sections };
};

// Why a section can't be duplicated right now, or null when it can.
export const duplicateSectionBlocked = (draft, sectionId) => {
    const s = (draft?.sections || []).find((x) => x.id === sectionId);
    if (!s) return 'That section no longer exists.';
    if (draft.sections.length >= CHECKLIST_LIMITS.sections) return `A checklist can have at most ${CHECKLIST_LIMITS.sections} sections.`;
    if (totalItems(draft) + s.questions.length > CHECKLIST_LIMITS.questions) return `Copying this section would go over ${CHECKLIST_LIMITS.questions} questions.`;
    return null;
};

// Duplicate section: a copy directly BELOW, titled "{title} (copy)" (or "Untitled (copy)"),
// every question copied with a NEW id unique in the template. Returns { draft, id } (id = the
// new section's), or the draft unchanged with id null when duplicateSectionBlocked says no.
export const duplicateSection = (draft, sectionId) => {
    if (duplicateSectionBlocked(draft, sectionId)) return { draft, id: null };
    const si = draft.sections.findIndex((x) => x.id === sectionId);
    const src = draft.sections[si];
    const taken = takenIds(draft);
    const sid = newId('s', taken);
    taken.add(sid);
    const questions = src.questions.map((q) => {
        const id = newId('q', taken);
        taken.add(id);
        return { ...q, id };
    });
    const suffix = ' (copy)';
    const title = `${(str(src.title).trim() || 'Untitled').slice(0, CHECKLIST_LIMITS.sectionTitle - suffix.length).trimEnd()}${suffix}`;
    const sections = [...draft.sections];
    sections.splice(si + 1, 0, { id: sid, title, questions });
    return { draft: { ...draft, sections }, id: sid };
};

// What switching a question's type would throw away, so the screen can ask first:
// 'choices' / 'unit' / null. Only text the person actually typed counts.
export const questionTypeChangeLoss = (question, type) => {
    if (!question || question.type === type) return null;
    if (OPTION_TYPES.includes(question.type) && !OPTION_TYPES.includes(type) && splitOptions(question.optionsText).length > 0) return 'choices';
    if (question.type === 'number' && str(question.unit).trim()) return 'unit';
    return null;
};

// Change a question's type. Keeps the question text. Drops what no longer fits (choices unless
// Pick one, the unit unless Number); Instructions turn Required off; a switch TO Pick one starts
// with no choices (validation then asks for them).
export const changeQuestionType = (draft, questionId, type) => {
    if (!QUESTION_TYPE_ORDER.includes(type)) return draft;
    return mapSections(draft, (s) => ({
        ...s,
        questions: s.questions.map((q) => {
            if (q.id !== questionId || q.type === type) return q;
            return {
                ...q,
                type,
                required: type === 'info' ? false : q.required === true,
                // chklstmulti-3e9b7c15 — Pick one <-> Pick as many keeps the choices; switches follow the type.
                optionsText: OPTION_TYPES.includes(type) && OPTION_TYPES.includes(q.type) ? q.optionsText : '',
                allowNote: canAllowNote(type) ? q.allowNote === true : false,
                allowPhotos: canAllowPhotos(type) ? q.allowPhotos === true : false,
                unit: type === 'number' ? str(q.unit) : '',
            };
        }),
    }));
};

// "Section 2 — Before you start" / "Section 3 — Untitled".
export const sectionLabel = (section, index) => `Section ${index + 1} — ${str(section?.title).trim() || 'Untitled'}`;

// ─── errors ─────────────────────────────────────────────────────────────────
// The version-guard refusal. The 100-checklist cap is ALSO failed-precondition, so the
// message decides.
export const isStaleTemplateError = (err) => {
    const code = String((err && err.code) || '');
    const msg = String((err && err.message) || '');
    return /failed-precondition/.test(code) && msg.includes(REFUSE_STALE_TEXT);
};
